import { useCallback, useRef, useState } from 'react'

import { DSButton } from '@vr/ds-react'

import { CustomDropdown } from '../custom-dropdown'
import { dropdownButtonStyle } from '../custom-dropdown/style'
import ModalMenuFiles from '../modal-menu-files'
import ModalReject from '../modal-reject'
import ModalRemove from '../modal-remove'
import ModalError from '@/components/modal-error'
import ModalDetails from '@/compositions/modal-details'
import ModalOperatorAccess from '@/compositions/modal-operator-access'
import { useDropdownPosition } from '@/hooks/use-dropdown-position'
import { tableActionsOptions } from '@/hooks/use-table-actions'

export const TableActionsDropdown = ({
  unit,
  pageResource,
  files,
  id,
  onClick,
  onRemoveSuccess
}: {
  unit?: {
    cpf: string
    id: string
    idOperadora: string
    idUnidade: string
    nomeOperadora: string
    numeroCartao: string
  }
  pageResource: string
  files?: string[]
  id?: string
  onClick?: () => void
  onRemoveSuccess?: () => void
}) => {
  const [activeModal, setActiveModal] = useState<
    null | 'details' | 'operatorAccess' | 'reject' | 'files' | 'report' | 'remove'
  >(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isEditable, setIsEditable] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState<any>(null)

  const buttonRef = useRef<HTMLButtonElement>(null!)
  const coords = useDropdownPosition(buttonRef, isOpen)

  const toggleDropdown = useCallback(() => setIsOpen(prev => !prev), [])
  const closeDropdown = useCallback(() => setIsOpen(false), [])

  const actionMap: Record<string, () => void> = {
    details: () => setActiveModal('details'),
    operatorAccess: () => setActiveModal('operatorAccess'),
    reject: () => setActiveModal('reject'),
    files: () => setActiveModal('files'),
    remove: () => setActiveModal('remove'),
    downloadReport: () => onClick?.()   
  }

  const handleActionClick = (action: string, onClick: () => void) => {
    const openModal = actionMap[action]
    if (openModal) openModal()
    onClick()
    closeDropdown()
  }

  const handleEditable = (isEditableFor: string) => {
    if (isEditableFor === pageResource) {
      setIsEditable(true)
    } else {
      setIsEditable(false)
    }
  }

  return (
    <>
      <DSButton
        iconLeft='principal-kebab'
        variant='ghost'
        onClick={toggleDropdown}
        ref={buttonRef}
        aria-haspopup='menu'
        aria-expanded={isOpen}
        size='small'
      />
      {isOpen && (
        <CustomDropdown
          top={coords.top}
          left={coords.left}
          onClose={closeDropdown}
        >
          {tableActionsOptions.map(
            ({ action, label, resource, isEditableFor, onClick }) =>
              resource.includes(pageResource) ? (
                <button
                  key={label}
                  onClick={() => {
                    handleActionClick(action, onClick)
                    handleEditable(isEditableFor)
                  }}
                  style={dropdownButtonStyle}
                >
                  {label}
                </button>
              ) : null
          )}
        </CustomDropdown>
      )}

      {activeModal === 'details' && (
        <ModalDetails
          unitId={unit.id}
          onClose={() => setActiveModal(null)}
          isEditable={isEditable}
          setShowErrorModal={setShowErrorModal}
        />
      )}

      {activeModal === 'operatorAccess' && (
        <ModalOperatorAccess
          unitId={unit.idUnidade}
          operatorId={unit.idOperadora}
          operadora={unit.nomeOperadora}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal === 'reject' && (
        <ModalReject
          id={unit.id}
          collectId={id}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal === 'files' && (
        <ModalMenuFiles
          onClose={() => setActiveModal(null)}
          files={files}
          unitId={unit.id}
        />
      )}

      {activeModal === 'remove' && (
        <ModalRemove
          ids={[unit.id]}
          onClose={() => setActiveModal(null)}
          onSuccess={onRemoveSuccess}
        />
      )}

      <ModalError
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
      >
        Não foi possível carregar os detalhes do item. Por favor, tente
        novamente mais tarde.
      </ModalError>
    </>
  )
}
